/**
 * BudgetResolver — Generalized budget lookup for any pivot combination.
 *
 * Budget data lives in a 2D matrix: Phase × Cost Type.  Resource_Code, Discipline,
 * Vendor, PO, etc. do NOT have their own budget dimension.
 *
 * The resolver walks a node's breadcrumb + ancestry to find every known Phase_Code
 * and Cost_Type_Code in the lineage, then intersects against the budget matrix to
 * return the correct slice of budget.
 *
 * Rules:
 *   1. If the ancestry contains BOTH a Phase and a Cost Type → budgetByPhaseCt[phase][ct]
 *   2. If only Phase is known → dayZeroBudget[phase]  (filtered to page cost types)
 *   3. If only Cost Type is known → costTypeBudget[ct]
 *   4. If neither is known → '' (no budget at this level)
 *   5. Non-budget columns (Resource, Discipline, Vendor, PO, Line) pass budget
 *      through from the intersection of whatever Phase/CostType is above them.
 *
 * For the page-level grand total, budget is the sum of costTypeBudget for only
 * the active page cost types — never the full phase budget.
 */

import { pNum } from '../db/company';

const norm = v => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim();
    return (s === 'undefined' || s === 'null') ? '' : s;
};

/**
 * Build the ancestry map for a given node by walking up the breadcrumb.
 * Returns { Phase_Code: '140403', Cost_Type_Code: '53100', ... } with whatever
 * columns are present in the breadcrumb path.
 *
 * @param {string} breadcrumb  – e.g. "^140403^53100^AcmeVendor"
 * @param {Array}  drillColumns – ordered column names, e.g. ['Phase_Code','Cost_Type_Code','Supplier_Name']
 */
const getAncestry = (breadcrumb, drillColumns) => {
    // Keep positional alignment (breadcrumbs start with '^') — a blank segment
    // (e.g. a budget row with no cost code) must not shift later columns onto
    // the wrong drill level.
    const segments = (breadcrumb || '').split('^').slice(1);
    const ancestry = {};
    for (let i = 0; i < Math.min(segments.length, drillColumns.length); i++) {
        if (segments[i]) {
            ancestry[drillColumns[i]] = segments[i];
        }
    }
    return ancestry;
};

// Columns that indicate budget is "split out" across vendors/POs/lines.
// When any of these appear in a node's ancestry, budget should NOT display
// on lower-level Phase/CostType nodes because the parent already shows the total.
const SPLIT_COLUMNS = new Set(['Supplier_Name', 'PO_Number', 'Line_ID']);

/**
 * Create a budget lookup function for a given set of budget data and drill config.
 *
 * @param {Object} opts
 * @param {Object} opts.dayZeroBudget     – phase → total budget (all cost types)
 * @param {Object} opts.costTypeBudget    – costType → total budget (all phases)
 * @param {Object} opts.budgetByPhaseCt   – phase → { costType → budget }
 * @param {Object} opts.budgetByCtPhase   – costType → { phase → budget }
 * @param {Array}  opts.drillColumns      – current drill order
 * @param {Array}  opts.pageCostTypes     – cost types this page cares about (e.g. ['53100','53200',...])
 * @param {Object} opts.reverseDisciplineMap – disciplineName → [phaseCodes] (optional)
 * @returns {Function} (node) → budget number or ''
 */
export const createBudgetResolver = ({
    dayZeroBudget = {},
    costTypeBudget = {},
    budgetByPhaseCt = {},
    budgetByCtPhase = {},
    budgetByResource = {},
    budgetByResourceCt = {},
    drillColumns = [],
    pageCostTypes = [],
    reverseDisciplineMap = {},
}) => {
    // Pre-compute phase budgets filtered to only this page's cost types
    // This prevents showing the full phase budget (incl labor/equipment) on a Materials page
    const phaseBudgetFiltered = {};
    Object.keys(dayZeroBudget).forEach(phase => {
        let filtered = 0;
        const phaseCts = budgetByPhaseCt[phase] || {};
        pageCostTypes.forEach(ct => {
            filtered += pNum(phaseCts[ct] || 0);
        });
        if (filtered !== 0) {
            phaseBudgetFiltered[phase] = filtered;
        }
    });

    // Pre-compute discipline budgets (sum of all phase budgets in each discipline)
    const disciplineBudgetFiltered = {};
    Object.keys(reverseDisciplineMap).forEach(disc => {
        let total = 0;
        (reverseDisciplineMap[disc] || []).forEach(phase => {
            total += pNum(phaseBudgetFiltered[phase] || 0);
        });
        if (total !== 0) {
            disciplineBudgetFiltered[disc] = total;
        }
    });

    // Pre-compute resource budgets filtered to this page's cost types
    const resourceBudgetFiltered = {};
    Object.keys(budgetByResourceCt).forEach(res => {
        let filtered = 0;
        pageCostTypes.forEach(ct => {
            filtered += pNum(budgetByResourceCt[res]?.[ct] || 0);
        });
        if (filtered !== 0) {
            resourceBudgetFiltered[res] = filtered;
        }
    });

    // Pre-compute "rest" budget for the empty-resource ("Unknown Resource") node:
    // page-cost-type total minus everything already attributed to a resource.
    // Per cost type: costTypeBudget[ct] − Σ budgetByResourceCt[*][ct]
    let restBudgetTotal = 0;
    const restBudgetByCt = {};
    pageCostTypes.forEach(ct => {
        let attributed = 0;
        Object.keys(budgetByResourceCt).forEach(res => {
            attributed += pNum(budgetByResourceCt[res]?.[ct] || 0);
        });
        const rest = pNum(costTypeBudget[ct] || 0) - attributed;
        if (rest !== 0) {
            restBudgetByCt[ct] = rest;
            restBudgetTotal += rest;
        }
    });

    /**
     * Resolve budget for a single tree node.
     *
     * @param {Object} node – tree node with node.data.{ path, key, breadcrumb, ... }
     * @returns {number|string} budget amount or '' if no budget at this level
     */
    return (node) => {
        if (!node?.data) return '';

        const { path: col, key, breadcrumb } = node.data;
        const ancestry = getAncestry(breadcrumb, drillColumns);

        // The current node adds its own column to the ancestry
        if (col && key) {
            ancestry[col] = key;
        }

        // ── Suppress budget below vendor/PO/line ──
        // If a split column is in the ancestry AND the current node is NOT
        // that split column itself, budget is meaningless (it's already shown above).
        const hasSplit = Object.keys(ancestry).some(k => SPLIT_COLUMNS.has(k));
        if (hasSplit) return '';

        const knownPhase = ancestry['Phase_Code'] || '';
        const knownCt = ancestry['Cost_Type_Code'] || '';
        const knownDisc = ancestry['Discipline'] || '';
        const knownRes = ancestry['Resource_Code'] || '';

        // ── "Unknown Phase" rollup ──
        // Budget rows saved without a cost code accumulate under the blank
        // phase key in the ledger. When Phase_Code is in the lineage but blank
        // (this node or an ancestor), surface that unattributed budget so the
        // drill still reconciles with the page total.
        const segments = (breadcrumb || '').split('^').slice(1);
        const phaseIdx = drillColumns.indexOf('Phase_Code');
        const phaseBlank = (col === 'Phase_Code' && !key) ||
            (phaseIdx >= 0 && phaseIdx < segments.length && segments[phaseIdx] === '');
        if (phaseBlank && !knownPhase) {
            if (knownCt) return budgetByPhaseCt['']?.[knownCt] ?? '';
            return phaseBudgetFiltered[''] ?? '';
        }

        // ── "Unknown Resource" rollup ──
        // When the current node IS a Resource_Code with empty key, surface the
        // rest of the page's budget that isn't attributed to any resource.
        if (col === 'Resource_Code' && !key) {
            if (knownCt) return restBudgetByCt[knownCt] ?? '';
            return restBudgetTotal || '';
        }

        // ── Case 1: Both Phase and Cost Type are known ──
        if (knownPhase && knownCt) {
            const val = budgetByPhaseCt[knownPhase]?.[knownCt];
            return val != null ? val : 0;
        }

        // ── Case 2: Only Phase is known ──
        // Return the phase budget filtered to this page's cost types
        if (knownPhase && !knownCt) {
            return phaseBudgetFiltered[knownPhase] ?? '';
        }

        // ── Case 3: Only Cost Type is known ──
        if (!knownPhase && knownCt) {
            // If we know the resource, slice budget to that resource × CT
            if (knownRes) {
                const val = budgetByResourceCt[knownRes]?.[knownCt];
                return val != null ? val : '';
            }
            // If we know the discipline, sum only that discipline's phases for this CT
            if (knownDisc && reverseDisciplineMap[knownDisc]) {
                let total = 0;
                reverseDisciplineMap[knownDisc].forEach(phase => {
                    total += pNum(budgetByPhaseCt[phase]?.[knownCt] || 0);
                });
                return total || '';
            }
            // Otherwise sum across all phases
            if (pageCostTypes.includes(knownCt)) {
                return costTypeBudget[knownCt] ?? '';
            }
            return '';
        }

        // ── Case 4: Only Discipline is known (no Phase or CT) ──
        if (knownDisc && reverseDisciplineMap[knownDisc]) {
            return disciplineBudgetFiltered[knownDisc] ?? '';
        }

        // ── Case 5: Only Resource is known (no Phase or CT) ──
        if (knownRes) {
            return resourceBudgetFiltered[knownRes] ?? '';
        }

        // ── Case 6: Nothing resolvable (Vendor/PO/Line at top level) ──
        return '';
    };
};

/**
 * Compute the correct grand total budget for the page.
 * Only sums cost types that belong to this page — never sums full phase budgets.
 *
 * @param {Object} costTypeBudget – costType → total budget
 * @param {Array}  pageCostTypes  – cost types for this page
 * @returns {number}
 */
export const computePageBudget = (costTypeBudget = {}, pageCostTypes = []) => {
    let total = 0;
    pageCostTypes.forEach(ct => {
        total += pNum(costTypeBudget[ct] || 0);
    });
    return total;
};

/**
 * Compute the grand total variance:  budget - totalTotal
 * (positive means under budget, negative means over).
 * Uses the same sign convention as the existing pages: budget - total.
 *
 * @param {number} pageBudget
 * @param {number} grandTotal (actualTotal + openTotal + etcTotal)
 * @returns {number}
 */
export const computeGrandVariance = (pageBudget, grandTotal) => {
    return pNum(pageBudget) - pNum(grandTotal);
};
